import { buildRequestOptionsJsonBody } from '@quintoandar/fwp-fetch';
import {
  deletePhotoURL,
  fetchPhotosURL,
  updateMainPhotoURL,
  rotatePhotoClockwiseURL,
  uploadFilePhotosphereURL,
  updatePhotosphereURL,
  fetchRoomsSuggestionsURL,
} from './urls';
import { MEDIA_BASE_URL } from './constants';
import { request } from '../utils/request-utils';

export const fetchPhotos = async (houseType, houseId, galleryType) =>
  request(fetchPhotosURL(houseType, houseId, galleryType), {
    method: 'GET',
  });

export const fetchRoomsSuggestions = async () =>
  request(fetchRoomsSuggestionsURL(), {
    method: 'GET',
  });

export const deletePhoto = async (id, galleryType, houseId) =>
  request(deletePhotoURL(id, galleryType, houseId), {
    method: 'DELETE',
  });

const COVER_WIDTH = 555;
const COVER_HEIGHT = 370;
const COVER_RATIO = COVER_WIDTH / COVER_HEIGHT;

const buildUpdateMainPhotoBody = (width, height) => {
  const ratio = width / height;

  let w;
  let h;

  if (ratio < COVER_RATIO) {
    w = COVER_WIDTH;
    h = Math.floor(COVER_WIDTH / ratio);
  } else {
    h = COVER_HEIGHT;
    w = Math.floor(COVER_HEIGHT * ratio);
  }

  if (w < COVER_WIDTH) {
    w = COVER_WIDTH;
    h = Math.floor(h / COVER_RATIO);
  }

  if (h < COVER_HEIGHT) {
    h = COVER_HEIGHT;
    w = Math.floor(w * COVER_RATIO);
  }

  const x = Math.floor((w - COVER_WIDTH) / 2);
  const y = Math.floor((h - COVER_HEIGHT) / 2);

  const params = new URLSearchParams({ x, y, w, h });

  return params.toString();
};

export const updateMainPhoto = async (id, houseId, width, height) =>
  request(updateMainPhotoURL(id, houseId), {
    method: 'PUT',
    body: buildUpdateMainPhotoBody(width, height),
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

export const rotatePhotoClockwise = async (propertyId, photoId, direction) =>
  request(rotatePhotoClockwiseURL(propertyId, photoId, direction), {
    method: 'PUT',
  });

export const uploadPhotosphere = async (houseType, houseId, file) => {
  const formData = new FormData();
  formData.append('image', file);

  try {
    const result = await request(
      uploadFilePhotosphereURL(houseType, houseId, 'photosphere'),
      {
        method: 'POST',
        body: formData,
      },
    );

    result.path = `${MEDIA_BASE_URL}${result.path}`;
    result.pathPreview = `${MEDIA_BASE_URL}${result.pathPreview}`;
    return result;
  } catch (e) {
    throw new Error(e.message, e.details);
  }
};

export const uploadNormalPhoto = async (houseType, houseId, file) => {
  const formData = new FormData();
  formData.append('imagens', file);

  await request(uploadFilePhotosphereURL(houseType, houseId, 'photo'), {
    method: 'POST',
    body: formData,
    headers: {},
  });

  // When an upload is over, we need photo's path and id.
  // Path is used to show photo, and id is necessary to delete this photo.
  // Upload endpoint does not return easy objects to manipulate.
  // So we upload photo and make a request for all photos, so that we have all data we need.

  const allPhotos = await fetchPhotos(houseType, houseId, 'photo');

  if (allPhotos && allPhotos.length) {
    return allPhotos[allPhotos.length - 1];
  }

  return {
    path: URL.createObjectURL(file),
    pathPreview: URL.createObjectURL(file),
  };
};

export const uploadPhoto = async (houseType, houseId, file, galleryType) => {
  if (galleryType === 'photo') {
    return uploadNormalPhoto(houseType, houseId, file);
  }
  return uploadPhotosphere(undefined, houseId, file);
};

export const updatePhotos = async (photos, galleryType, houseId) =>
  request(
    updatePhotosphereURL(galleryType, houseId),
    buildRequestOptionsJsonBody('PATCH', {
      images: photos.map((photo, index) => ({
        id: photo.id,
        order: index,
        caption: photo.description,
      })),
    }),
  );
